'use client'

import { ArrowLeft24, X24 } from '@vinted/monochrome-icons'
import { Button, Cell, Dialog, Divider, Image, Navigation, Radio, Text } from '@vinted/web-ui'
import { useContext, useEffect } from 'react'

import { GetShippingLabelContext } from '@marketplace-web/shipping-services-provider/shipping-services-provider-feature'
import { useConversationContext } from '@marketplace-web/messaging/conversation-providers-feature'
import { useFetch } from '@marketplace-web/shared/api-client'
import { useTracking } from '@marketplace-web/observability/event-tracker-data'
import { useTranslate } from '@marketplace-web/shared/i18n'
import { List } from '@marketplace-web/common-components/list-ui'
import { ContentLoader } from '@marketplace-web/common-components/content-loader-ui'
import {
  GenerateLabelModal,
  DropOffTypeKey,
  transformLabelOptionsResponse,
  getLabelOptions,
  LabelOptionsDropOffTypesModel,
  ShipmentEntryType,
} from '@marketplace-web/conversation-shipping/conversation-shipping-data'

import { clickEvent } from '_libs/common/event-tracker/events'
import { ClickableElement } from 'constants/tracking/clickable-elements'
import { Screen } from 'constants/tracking/screens'

type Props = {
  isOpen: boolean
  onClose: () => void
  onSuccess: () => void
}

const DropOffTypeSelectionModal = ({ isOpen, onClose, onSuccess }: Props) => {
  const { track } = useTracking()
  const dropOffTypeTranslate = useTranslate(
    'conversation.get_shipping_label.select_drop_off_method.modal',
  )
  const packagingTypeTranslate = useTranslate(
    'conversation.get_shipping_label.select_packaging_type.modal',
  )
  const a11yTranslate = useTranslate(
    'conversation.get_shipping_label.select_drop_off_method.modal.a11y.actions',
  )

  const {
    state: {
      labelTypeSelection: { clearLabelTypeSelection },
    },
  } = useContext(GetShippingLabelContext)
  const { conversation } = useConversationContext()
  const {
    state: {
      activeModal: { setActiveModal },
    },
  } = useContext(GetShippingLabelContext)

  const shipmentId = conversation?.transaction?.shipment?.id
  const transactionId = conversation?.transaction?.id

  const {
    state: {
      dropOffTypeSelection: {
        selectedDropOffTypeKey,
        isSubmitEnabled,
        clearDropOffTypeSelection,
        selectDropOffTypeKey,
      },
    },
  } = useContext(GetShippingLabelContext)

  const {
    fetch: fetchLabelOptions,
    transformedData: labelOptions,
    isLoading: isLabelOptionsLoading,
  } = useFetch(getLabelOptions, transformLabelOptionsResponse)

  useEffect(() => {
    if (shipmentId && isOpen) fetchLabelOptions(shipmentId)
  }, [shipmentId, isOpen, fetchLabelOptions])

  const isPackageTypeLabelTypeFlow =
    labelOptions?.availableLabelTypes &&
    labelOptions?.availableLabelTypes.length > 1 &&
    labelOptions?.dropOffTypes &&
    labelOptions?.dropOffTypes.length > 1
  const isReturnShipment = labelOptions?.shipmentEntryType === ShipmentEntryType.Return

  function renderCarrier() {
    if (!labelOptions?.carrier) return null

    return (
      <Cell
        divider
        prefix={
          <Image
            src={labelOptions?.carrier.iconUrl}
            size={Image.Size.Large}
            styling={Image.Styling.Rounded}
            alt={labelOptions?.carrier.name}
          />
        }
        title={labelOptions?.carrier.name}
        body={!isReturnShipment && dropOffTypeTranslate('carrier_body')}
      />
    )
  }

  const selectDropOffTypeMethod = (selectedId: string | number) => () => {
    selectDropOffTypeKey(selectedId)
  }

  function trackSubmitButtonClick() {
    const targetDetails = {
      transaction_id: transactionId,
      dropoff_type_key: selectedDropOffTypeKey,
    }

    track(
      clickEvent({
        target: ClickableElement.SelectDropoffType,
        screen: Screen.DropoffTypeSelection,
        targetDetails: JSON.stringify(targetDetails),
      }),
    )
  }

  function handleSubmit() {
    trackSubmitButtonClick()

    onSuccess()
  }

  function renderLabelOptionsDropOffTypeMethod(dropOffTypeMethod: LabelOptionsDropOffTypesModel) {
    return (
      <Cell
        key={dropOffTypeMethod.key}
        type={Cell.Type.Navigating}
        prefix={
          <Image
            src={dropOffTypeMethod.dropOffTypeMeta.iconUrl}
            size={Image.Size.Regular}
            styling={Image.Styling.Rounded}
            alt={dropOffTypeMethod.title}
          />
        }
        title={dropOffTypeMethod.title}
        body={<Text as="span" html text={dropOffTypeMethod.subtitle} />}
        onClick={selectDropOffTypeMethod(dropOffTypeMethod.key)}
        suffix={
          <Radio
            name={`${dropOffTypeMethod.key}-radio`}
            checked={dropOffTypeMethod.key === selectedDropOffTypeKey}
            value={dropOffTypeMethod.key}
            testId={`drop-off-type-selection-modal-${dropOffTypeMethod.key}-radio`}
            onChange={() => undefined}
            aria={{
              'aria-labelledby': `drop-off-type-selection-modal-${dropOffTypeMethod.key}-radio`,
            }}
          />
        }
      />
    )
  }

  function renderBodyText() {
    if (!labelOptions?.dropOffTypes) return null

    const isReusablePackaging = labelOptions?.dropOffTypes
      .map(({ key }) => key)
      .includes(DropOffTypeKey.ReusablePackage)

    return isReusablePackaging ? packagingTypeTranslate('body') : dropOffTypeTranslate('body')
  }

  function renderDropOffTypeSelection() {
    return (
      <>
        <Cell body={<Text as="h1" type={Text.Type.Heading} text={renderBodyText()} />} />
        <List>{labelOptions?.dropOffTypes?.map(renderLabelOptionsDropOffTypeMethod)}</List>
        <Divider />
      </>
    )
  }

  function renderSubmit() {
    return (
      <div className="drop-off-selection-flow__button">
        <Divider />
        <Cell>
          <Button
            styling={Button.Styling.Filled}
            text={dropOffTypeTranslate('actions.continue')}
            disabled={!isSubmitEnabled}
            onClick={handleSubmit}
            testId="drop-off-type-selection-modal-submit"
          />
        </Cell>
      </div>
    )
  }

  function handleClose() {
    clearDropOffTypeSelection()
    clearLabelTypeSelection()
    onClose()
  }

  function handleLabelOptionsPreviousClick() {
    if (isReturnShipment) {
      clearDropOffTypeSelection()
      setActiveModal(GenerateLabelModal.ReturnTheOrder)

      return
    }

    if (!isReturnShipment && isPackageTypeLabelTypeFlow) {
      setActiveModal(GenerateLabelModal.DigitalLabelTypeSelectionModal)
    }
  }

  function isBackArrowVisible() {
    if (isReturnShipment) {
      return true
    }

    if (isPackageTypeLabelTypeFlow) {
      return true
    }

    return false
  }

  function renderNavigationBackArrow() {
    if (!isBackArrowVisible()) return null

    return (
      <Button
        styling={Button.Styling.Flat}
        iconName={ArrowLeft24}
        inline
        onClick={handleLabelOptionsPreviousClick}
        testId="selection-modal-previous"
        aria={{ 'aria-label': a11yTranslate('back') }}
      />
    )
  }

  function renderModalContent() {
    if (isLabelOptionsLoading) return <ContentLoader testId="drop-off-selection-modal-loader" />

    return (
      <>
        {renderCarrier()}
        {renderDropOffTypeSelection()}
        {renderSubmit()}
      </>
    )
  }

  return (
    <Dialog show={isOpen}>
      <div className="drop-off-selection-flow__body">
        <Navigation
          body={dropOffTypeTranslate('title')}
          left={renderNavigationBackArrow()}
          right={
            <Button
              styling={Button.Styling.Flat}
              onClick={handleClose}
              iconName={X24}
              testId="close-selection-modal"
              aria={{ 'aria-label': a11yTranslate('close') }}
              inline
            />
          }
        />
        <Divider />
        {renderModalContent()}
      </div>
    </Dialog>
  )
}

export default DropOffTypeSelectionModal
